'use client';

import { FC, useEffect, useMemo, useState } from 'react';

import { ElasticUtils } from '@ravago/shared/next-data-access/client';
import { FeatureToggle, RoleBasedToggle, UnitSystem } from '@ravago/shared/page-data/models/config';
import { NormalizedCountry, NormalizedUnitOfMeasure } from '@ravago/shared/page-data/models/contentful-mapped';
import {
   AutoSuggestQueryArgs,
   AutoSuggestResponse,
   FacetFilterTracking,
   FacetFilterWithTerms,
   facetLabelTranslationMapping,
   OverviewPageItem,
   ProductRequestType,
   ProductsQueryResponse,
   viewMap,
   ViewType
} from '@ravago/shared/page-data/models/elastic';
import { ProductPortfolioNode } from '@ravago/shared/page-data/models/elements';
import { RendererGlobals } from '@ravago/shared/page-data/models/utils';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { BreakpointSwitch } from '@ravago/shared/radiance/components/breakpoint-switch/components/BreakpointSwitch/BreakpointSwitch';
import { Flyout } from '@ravago/shared/radiance/components/flyout/components/Flyout/Flyout';
import { Grid } from '@ravago/shared/radiance/components/grid/components/Grid/Grid';
import { GridCol } from '@ravago/shared/radiance/components/grid/components/GridCol/GridCol';
import { useBreakpoint } from '@ravago/shared/radiance/hooks/useBreakpoint/useBreakpoint';
import notification from '@ravago/shared/radiance/utils/notification/notification';
import { useTranslations } from '@ravago/shared/translations/client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import isEqual from 'lodash/isEqual';

import { CorrelationService } from '../../../../services/tracking/correlation-token.service';
import { getLanguageCode } from '../../../../utils/locale/locale.utils';
import { TrackingUtils } from '../../../../utils/tracking/tracking.utils';
import { unitSystems } from '../../../shared/physical-property-display/mappers/unit-systems.mapper';
import { ProductCompareSelection } from '../../product-compare/components/product-compare-selection';
import { AutoSuggestClientRequest, SearchClientRequest } from '../models/client-request.model';
import { FacetGroup, toFacetGroup } from '../models/facet-group.model';
import { filterToQuery } from '../utils/filter-query-utils';

import styles from './product-portfolio.module.scss';
import { ProductPortfolioEmpty } from './ui/portfolio/product-portfolio-empty';
import { ProductPortfolioFilters } from './ui/portfolio/product-portfolio-filters';
import { ProductPortfolioGrid } from './ui/portfolio/product-portfolio-grid';
import { ProductPortfolioSearch } from './ui/portfolio/product-portfolio-search';
import { ProductPortfolioTable } from './ui/portfolio/product-portfolio-table-view';
import { ProductPortfolioTabs } from './ui/portfolio/product-portfolio-tabs';

const tabMap: { [view: string]: string } = {
   product: 'products',
   brand: 'brands',
   caseStudy: 'case-study'
};

interface ViewInfo {
   viewType: 'list' | 'grid';
   columnSelection?: string[];
   uom?: UnitSystem;
}

const AUTO_SUGGEST_SIZE = 70;

const getAutoSuggestResult = ({ facets, search, correlationId, language }: AutoSuggestClientRequest): Promise<AutoSuggestResponse> => {
   const request: AutoSuggestQueryArgs = {
      autoSuggestRequest: {
         facets,
         query: search.trim()
      },
      size: AUTO_SUGGEST_SIZE,
      language
   };

   return ElasticUtils.getAutosuggestResult(request, correlationId);
};

interface Props {
   baseRoutes?: {
      brand: string;
      caseStudy: string;
      product: string;
      market: string;
      technology: string;
   };
   relatedDocumentsAnchor?: string;
   node: ProductPortfolioNode;
   globals: RendererGlobals;
   hasListViewPermission?: boolean;
   hasPhysicalPropertiesPermission?: boolean;
   hasPublicLabelAndSigningsPermission?: boolean;
   hasSustainabilityLabels?: boolean;
   channelUOMs?: NormalizedUnitOfMeasure[];
   initialSearchType: ProductRequestType;
   initialView: ViewType;
   initialPage: number;
   initialSearch: string;
   initialFacetFilters: FacetFilterWithTerms[];
   initialTotals: Record<ProductRequestType, number | undefined>;
   baseFacetFilters: FacetFilterWithTerms[];
   countries: Array<NormalizedCountry>;
   getSearchResults: (request: SearchClientRequest) => Promise<ProductsQueryResponse<OverviewPageItem>>;
   featureToggles: FeatureToggle[];
   roleBasedToggles: RoleBasedToggle;
}

export const ProductPortfolio: FC<Props> = ({
   baseRoutes,
   relatedDocumentsAnchor,
   node,
   globals,
   hasListViewPermission = false,
   channelUOMs = [],
   hasPhysicalPropertiesPermission = false,
   hasPublicLabelAndSigningsPermission = false,
   hasSustainabilityLabels = false,
   initialSearchType,
   initialPage,
   initialSearch,
   initialView,
   initialFacetFilters,
   initialTotals,
   baseFacetFilters,
   countries,
   getSearchResults,
   featureToggles,
   roleBasedToggles
}) => {
   const translate = useTranslations();

   const { desktop, phone } = useBreakpoint();

   const language = getLanguageCode(globals.locale);
   const getViewInfoFromSessionStorage = () => {
      const viewInfoFromSession = sessionStorage.getItem('view-info');
      if (viewInfoFromSession) {
         return JSON.parse(viewInfoFromSession);
      }
      return undefined;
   };

   const units = unitSystems(translate);
   const defaultUom = channelUOMs.find((uom) => uom.default)?.id === 'imperial' ? 'US' : 'SI';
   const [unitSystem, setUnitSytem] = useState<UnitSystem>(units.find((unit) => unit.id === defaultUom)?.id ?? units[0]?.id);

   // Tracking state to know when to trigger tracking events
   const [trackedSearch, setTrackedSearch] = useState<string>('');
   const [trackFacetsAndSearchBeforeReset, setTrackFacetsAndSearchBeforeReset] = useState<
      { facets: FacetFilterWithTerms[]; search: string; page: number } | undefined
   >();
   const [trackFilterClick, setTrackFilterClick] = useState<FacetFilterTracking | undefined>(undefined);

   // Search & Filter State
   const [search, setSearch] = useState(initialSearch);
   const [activeColumns, setActiveColumn] = useState<string[] | undefined>();
   const [autoSuggestSearch, setAutoSuggestSearch] = useState(initialSearch);
   const [searchType, setSearchType] = useState<ProductRequestType>(initialSearchType);
   const [viewType, setViewType] = useState<{ view: ViewType; size: number }>({
      view: initialView,
      size: viewMap[initialView]
   });
   const [page, setPage] = useState(initialPage);
   const [facets, setFacets] = useState<FacetFilterWithTerms[]>(initialFacetFilters);
   const [totals, setTotals] = useState<Record<ProductRequestType, number | undefined>>(initialTotals);
   const [autoSuggestExpanded, setAutoSuggestExpanded] = useState(false);
   const [expandedGroups, setExpandedGroups] = useState<Array<string>>([]);
   const [isFetchingNewPage, setIsFetchingNewPage] = useState(false);

   const canResetFilters = !isEqual(baseFacetFilters, facets);

   const searchQuery = useQuery({
      queryKey: [
         'product-portfolio',
         {
            facets,
            search,
            page,
            tab: searchType,
            view: viewType.view
         }
      ],
      queryFn: () =>
         getSearchResults({
            facets,
            search,
            page,
            searchType,
            size: viewType.size,
            language,
            correlationId: CorrelationService.generateCorrelationId('dpcid')
         }),
      placeholderData: keepPreviousData,
      refetchOnMount: false,
      refetchOnWindowFocus: false
   });

   const trackSearch = (allTotals: Record<ProductRequestType, number | undefined>) => {
      TrackingUtils.track('product-portfolio-search', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         searchQuery: search,
         totalAmountOfItems: JSON.stringify(allTotals),
         activeFacets: JSON.stringify(TrackingUtils.translateFacets([...facets], translate)),
         language
      });
      setTrackedSearch(search);
   };

   const trackFilterReset = () => {
      if (!trackFacetsAndSearchBeforeReset) return;
      TrackingUtils.track('click_reset_all_filters', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         searchQueryBeforeReset: trackFacetsAndSearchBeforeReset.search,
         facetsBeforeReset: JSON.stringify(TrackingUtils.translateFacets([...trackFacetsAndSearchBeforeReset.facets], translate)),
         pageNumberBeforeReset: trackFacetsAndSearchBeforeReset.page.toString()
      });
      setTrackFacetsAndSearchBeforeReset(undefined);
   };

   const trackFacets = (allTotals: Record<ProductRequestType, number | undefined>) => {
      TrackingUtils.track('click_filter_facet', {
         correlationId: CorrelationService.getCorrelationId('dpcid'),
         selectedFilters: JSON.stringify(TrackingUtils.translateFacets([...facets], translate)),
         lastSelectedFilter: JSON.stringify({
            ...trackFilterClick,
            facet: trackFilterClick?.facet
               ? translate(facetLabelTranslationMapping.get(trackFilterClick.facet) ?? trackFilterClick.facet, undefined, true)
               : undefined
         }),
         totalAmountOfItems: JSON.stringify(allTotals)
      });
      setTrackFilterClick(undefined);
   };

   useEffect(() => {
      if (!searchQuery.data?.documentCounts) return;
      const allTotals = {
         product: searchQuery.data.documentCounts.products,
         brand: searchQuery.data.documentCounts.brands,
         caseStudy: searchQuery.data.documentCounts.caseStudies
      };
      setTotals(allTotals);
      // Only track the search when the app gives the user a result and when the search changes
      if (search !== trackedSearch) trackSearch({ ...allTotals });
      // Only track the facets when the app gives the user a result and when a facet is added
      if (trackFilterClick) trackFacets({ ...allTotals });
      // Track the reset of the filters
      if (trackFacetsAndSearchBeforeReset) trackFilterReset();
   }, [searchQuery.data]);

   const autoSuggestQuery = useQuery({
      queryKey: ['auto-suggest', { facets, search: autoSuggestSearch }],
      queryFn: async ({ signal }) => {
         await new Promise((resolve) => {
            setTimeout(resolve, 500);
         });
         if (signal.aborted) {
            return undefined;
         }
         return getAutoSuggestResult({
            facets,
            search: autoSuggestSearch,
            correlationId: CorrelationService.generateCorrelationId('dpcid'),
            language
         });
      },
      enabled: () => autoSuggestSearch.length > 1 && autoSuggestExpanded
   });

   // UI State
   const [filterFlyoutOpen, setFilterFlyoutOpen] = useState(false);

   const facetGroups: FacetGroup[] = useMemo(() => {
      if (!searchQuery.data) return [];
      return (
         searchQuery.data.facets
            .map((facet) => toFacetGroup(facet, language, countries))
            // filter out the physical properties from the facets
            .filter(
               (facet) =>
                  !['notchedImpact.value.raw', 'flameRating.value.raw', 'flowRate.value.raw', 'density.value.raw'].includes(facet.identifier.key)
            )
      );
   }, [searchQuery.data, language, countries]);

   const filtersVisible = useMemo(() => facets.length > 0 || (searchQuery.data && searchQuery.data.facets.length > 0), [facets, searchQuery.data]);

   const updateQueryString = (searchType: ProductRequestType, page: number, search: string, facets: FacetFilterWithTerms[], view = 'grid') => {
      const params = new URLSearchParams();

      params.set('tab', tabMap[searchType]);
      if (hasListViewPermission) params.set('view', view.toString());

      if (search) params.set('search', search);
      if (facets.length > 0) params.set('filter', filterToQuery(facets));
      params.set('page', page.toString());

      window.history.pushState(null, '', `?${params.toString()}`);
   };

   useEffect(() => {
      // initialize the view info from session storage on client side
      const sessionData = getViewInfoFromSessionStorage();

      if (sessionData?.uom) setUnitSytem(sessionData.uom);
      if (sessionData?.columnSelection) setActiveColumn(sessionData.columnSelection);
      if (sessionData?.viewType) setViewType({ view: sessionData.viewType, size: viewMap[sessionData.viewType] });
   }, []);

   useEffect(() => {
      if (searchQuery.error) {
         notification(translate('products-overview-error'));
      }
   }, [searchQuery.error, language]);

   useEffect(() => {
      if (desktop) setFilterFlyoutOpen(false);
   }, [desktop]);

   const onSearchChange = (query: string) => {
      setAutoSuggestSearch(query);
   };

   const saveViewInfoToSessionStorage = (viewInfo: ViewInfo) => {
      sessionStorage.setItem('view-info', JSON.stringify(viewInfo));
   };

   const switchUnitSystem = (value?: 'SI' | 'US') => {
      if (value) {
         setUnitSytem(value);
         saveViewInfoToSessionStorage({ viewType: viewType.view, columnSelection: activeColumns, uom: value });
      }
   };

   const onSearch = (query: string) => {
      setSearch(query);
      setPage(1);
      updateQueryString(searchType, 1, query, facets, viewType.view);
   };

   const onTabChange = (newTab: ProductRequestType) => {
      setExpandedGroups([]);
      setSearchType(newTab);
      setPage(1);

      updateQueryString(newTab, 1, search, facets, viewType.view);
   };

   const onPageChange = () => {
      setIsFetchingNewPage(true);
   };

   const onActiveColumnUpdate = (newActiveColumns: string[]) => {
      setActiveColumn(newActiveColumns);
      saveViewInfoToSessionStorage({
         viewType: viewType.view ?? 'list',
         columnSelection: newActiveColumns,
         uom: unitSystem
      });
   };

   const updateViewType = (view: string) => {
      if (!hasListViewPermission) return;

      let newViewType: ViewType = 'grid';
      if (view === 'list') {
         newViewType = 'list';
         saveViewInfoToSessionStorage({ viewType: newViewType, columnSelection: activeColumns, uom: unitSystem });
      } else {
         saveViewInfoToSessionStorage({ viewType: newViewType });
      }

      updateQueryString(searchType, 1, search, facets, newViewType);
      setViewType({ view: newViewType, size: viewMap[newViewType] });
   };

   const facetChange = (newFacets: FacetFilterWithTerms[]) => {
      setFacets([...newFacets]);
      setPage(1);

      updateQueryString(searchType, 1, search, [...newFacets], viewType.view);
   };

   const facetReset = () => {
      setTrackFacetsAndSearchBeforeReset({ facets: [...facets], search, page });
      setFacets([...baseFacetFilters]);
      setSearch('');
      setPage(1);

      updateQueryString(searchType, 1, '', baseFacetFilters, viewType.view);
   };

   const onExpand = (key: string, expanded: boolean) => {
      if (expanded) {
         setExpandedGroups([...expandedGroups, key]);
      } else {
         setExpandedGroups(expandedGroups.filter((group) => group !== key));
      }
   };

   const filters = searchQuery.data ? (
      <ProductPortfolioFilters
         facetFilters={facets}
         facets={facetGroups}
         canResetFilters={canResetFilters}
         filterFlyoutOpen={filterFlyoutOpen}
         language={language}
         locked={searchQuery.isPlaceholderData}
         expandedGroups={expandedGroups}
         onFilterAdjust={facetChange}
         onFilterReset={facetReset}
         onFilterClicked={(value) => setTrackFilterClick(value)}
         onExpandChange={onExpand}
         onCloseAll={() => setExpandedGroups([])}
         onFilterFlyoutClose={() => setFilterFlyoutOpen(false)}
      />
   ) : null;

   return (
      <>
         <Box gap="lg" direction="column">
            <Box gap="md" direction="column">
               <ProductPortfolioSearch
                  onAutoSuggestMenuChange={(value) => setAutoSuggestExpanded(value)}
                  autoSuggestExpanded={autoSuggestExpanded}
                  language={language}
                  numberOfFilters={facets.length}
                  autoSuggestResult={autoSuggestQuery.data}
                  loading={autoSuggestQuery.isFetching}
                  baseRoutes={baseRoutes}
                  globals={globals}
                  facetFilters={facets}
                  onFiltersChange={facetChange}
                  onFilterFlyoutOpen={() => setFilterFlyoutOpen(true)}
                  onSearchChange={onSearchChange}
                  onSearch={onSearch}
                  data-testid="product-portfolio__search"
                  featureToggles={featureToggles}
               />
               <ProductPortfolioTabs
                  onTabChange={onTabChange}
                  switchView={updateViewType}
                  searchType={searchType}
                  viewType={viewType.view}
                  allowListViewOption={hasListViewPermission && !phone}
                  totals={totals}
                  features={featureToggles}
                  locked={searchQuery.isPlaceholderData}
                  unitSystem={unitSystem}
                  onUomSwitch={switchUnitSystem}
                  allowShowPhyiscalProperties={hasPhysicalPropertiesPermission}
                  listViewActive={viewType.view === 'list'}
               />
            </Box>

            {searchQuery.data && (
               <Grid useContainerQuery={!globals.isConsumer}>
                  {filtersVisible && (
                     <BreakpointSwitch includeViews={['desktop']}>
                        <GridCol spanDesktop={3} spanTablet={0} spanPhone={0}>
                           {filters}
                        </GridCol>
                     </BreakpointSwitch>
                  )}
                  <GridCol spanDesktop={filtersVisible ? 9 : 12} spanTablet={12} spanPhone={4} useContainerQuery={!globals.isConsumer}>
                     {roleBasedToggles.hasProductCompareFeatureToggle && searchType === 'product' && (
                        <Box spacing={{ bottom: 'lg' }}>
                           <ProductCompareSelection locale={globals.locale} />
                        </Box>
                     )}
                     {searchQuery.data.content.length === 0 ? (
                        <ProductPortfolioEmpty searchTerm={search} />
                     ) : (
                        <div
                           className={styles.productPortfolio__grid}
                           style={{
                              opacity: searchQuery.isPlaceholderData || isFetchingNewPage ? 0.5 : 1,
                              pointerEvents: searchQuery.isPlaceholderData || isFetchingNewPage ? 'none' : 'unset'
                           }}
                        >
                           {viewType.view === 'grid' || searchType !== 'product' || !hasListViewPermission || phone ? (
                              <ProductPortfolioGrid
                                 onPageChange={onPageChange}
                                 baseRoutes={baseRoutes}
                                 roleBasedToggles={roleBasedToggles}
                                 relatedDocumentsAnchor={relatedDocumentsAnchor}
                                 content={searchQuery.data.content}
                                 hasPublicLabelAndSigningsToggles={hasPublicLabelAndSigningsPermission}
                                 hasSustainabilityLabels={hasSustainabilityLabels}
                                 page={page}
                                 totalPages={searchQuery.data.totalPages}
                                 node={node}
                                 globals={globals}
                              />
                           ) : (
                              <ProductPortfolioTable
                                 unitSystem={unitSystem}
                                 roleBasedToggles={roleBasedToggles}
                                 baseRoutes={baseRoutes}
                                 content={searchQuery.data.content}
                                 hasPublicLabelAndSigningsToggles={hasPublicLabelAndSigningsPermission}
                                 allowShowPhyiscalProperties={hasPhysicalPropertiesPermission}
                                 hasSustainabilityLabels={hasSustainabilityLabels}
                                 activeColumnsUpdate={onActiveColumnUpdate}
                                 activeColumns={activeColumns}
                                 onPageChange={onPageChange}
                                 page={page}
                                 totalPages={searchQuery.data.totalPages}
                                 globals={globals}
                              />
                           )}
                        </div>
                     )}
                  </GridCol>
               </Grid>
            )}
         </Box>

         {searchQuery.data && (
            <BreakpointSwitch excludeViews={['desktop']}>
               <Flyout open={filterFlyoutOpen} onFlyoutClose={() => setFilterFlyoutOpen(false)}>
                  {filters}
               </Flyout>
            </BreakpointSwitch>
         )}
      </>
   );
};
